import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import mixpanel from 'mixpanel-browser';
import Conditions from './components/Conditions';
import Supplements from './components/Supplements';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import CreateStack from './components/CreateStack';
import ManualCreateStack from './components/ManualCreateStack';
import EditStack from './components/EditStack';
import AccountDeleted from './pages/AccountDeleted';
import SubscriptionComplete from './pages/SubscriptionComplete';
import BottomNav from './components/BottomNav';
import ViewStack from './components/ViewStack';
import ContactUs from './components/ContactUs';
import Footer from './components/Footer';
import { LC } from './components/landing/landingTokens';
import PrivacyPolicy from './components/PrivacyPolicy';
import { useAuth, AuthProvider } from './AuthContext';
import InteractionCheckerPage from './components/interactionChecker/InteractionCheckerPage';
import LandingPage from './components/LandingPage';
import VerifyEmail from './components/VerifyEmail';
import './App.css';
import TermsOfUse from './components/TermsOfUse';
import Detail from './components/Detail';
import Article from './components/Article';
import Research from './components/Research';
import Login from './components/auth/Login';
import Register from './components/user/Register';
import RequireAuth from './components/auth/RequireAuth';
import RegistrationCompletion from './components/auth/RegistrationCompletion';
import ProductsExplorer from './components/products/ProductsExplorer';
import ProductDetail from './components/products/ProductDetail';
import { HelmetProvider } from 'react-helmet-async';
import config from './config';
import BlogPage from './components/blog/BlogPage';
import BlogPostPage from './components/blog/BlogPostPage';
import { FooterProvider } from './contexts/FooterContext';

import { UserProvider } from './UserContext';
import { StackGenerationProvider } from './contexts/StackGenerationContext';
import ProfilePageRedesign from './components/user/profile-redesign/ProfilePageRedesign';
import MedicationSupportView from './components/MedicationSupportView';
import DietSupportView from './components/DietSupportView';
import SubscriptionRequired from './components/subscription/SubscriptionRequired';
import LapsedUserNotifications from './components/common/LapsedUserNotifications';
import UpdateNotification from './components/common/UpdateNotification';
import VersionCheckService from './services/VersionCheckService';
import AboutUs from './components/AboutUs';
import DownloadPage from './components/DownloadPage';
import HealthPage from './components/HealthPage';
import { isHealthPageEnabled } from './utils/healthRoute';
import { identifyUser, setSubscriptionStatus, captureGoogleAdsAttribution, getGoogleAdsAttributionProps, forgetLegacyMixpanelIdentity } from './utils/analytics';
import useSoftwareKeyboard from './hooks/useSoftwareKeyboard';

// Expose VersionCheckService to window for debugging
if (typeof window !== 'undefined') {
  window.VersionCheckService = VersionCheckService;
}

function CuratedStackRedirect() {
  const { goalSlug } = useParams();
  const goalName = config.PRESET_HEALTH_GOALS?.[goalSlug] || goalSlug;
  return <Navigate to={`/create-stack?healthGoals=${encodeURIComponent(goalName)}&auto_generate=true`} replace />;
}

// Get Mixpanel token from environment variable
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT; // To distinguish environments
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL; // Get Supabase URL from env

function App() {
  const location = useLocation();
  const { currentUser, loading } = useAuth();
  const analyticsUidRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSoftwareKeyboardOpen = useSoftwareKeyboard();
  const showAffiliateStore = config.FEATURES.AFFILIATE_STORE;
  const [appPlatform, setAppPlatform] = useState('web');
  const isDownloadPage = location.pathname === '/download';
  const showMobileBottomNav =
    isMobile && !isDownloadPage && !isSoftwareKeyboardOpen;
  // Logged-out marketing landing: the global Header floats over the hero
  // (paddingTop exemption below lets the hero gradient bleed up behind it).
  const isPublicLanding = location.pathname === '/' && !currentUser;

  // Define routes that should have footer hidden by default
  const footerHiddenRoutes = useMemo(() => ['/create-stack'], []);
  
  // Function to check if current path should have footer hidden
  const shouldHideFooter = (pathname) => {
    return footerHiddenRoutes.some(route => pathname.startsWith(route));
  };

  useEffect(() => {
    // --- Mixpanel Initialization ---
    if (MIXPANEL_TOKEN) {
      let apiHost = ""; // Default empty, SDK will use Mixpanel's default
      
      // Logic to determine if proxy should be used
      const useProxy = 
        (APP_ENVIRONMENT === "staging" || APP_ENVIRONMENT === "production") || 
        (APP_ENVIRONMENT === "development" && SUPABASE_URL === "https://pokupvxcrypwhhzlossb.supabase.co");

      if (useProxy && SUPABASE_URL) {
        apiHost = `${SUPABASE_URL}/functions/v1/appEvents`;
      }

      const mixpanelConfig = {
        debug: APP_ENVIRONMENT === "development", // Enable debug mode for development
        persistence: "localStorage",
        track_pageview: true, // Automatically track page views in Mixpanel
        ignore_dnt: true, // Ignore browser DNT setting
      };

      if (apiHost) {
        mixpanelConfig.api_host = apiHost;
      }

      // Before init: init sends the first page view (STACK-594).
      forgetLegacyMixpanelIdentity(MIXPANEL_TOKEN);
      mixpanel.init(MIXPANEL_TOKEN, mixpanelConfig);
      
      // Only log in development
      if (APP_ENVIRONMENT === "development") {
        console.log(`Mixpanel initialized for development. API host: ${apiHost || 'Mixpanel Default'}`);
      }
    } else if (APP_ENVIRONMENT === "development") {
      console.warn("Mixpanel token not found. Tracking disabled.");
    }
    // --- End Mixpanel Initialization ---

    let platform = 'web';
    if (window.webkit?.messageHandlers?.iOSInterface) {
      platform = 'ios';
    } else if (window.AndroidInterface) {
      platform = 'android';
    }
    
    setAppPlatform(platform);
    window.appPlatform = platform; // Expose platform globally

    // --- Mixpanel User Tracking (moved from auth listener) ---
    // Let AuthContext handle authentication, we just track user changes for analytics
    if (MIXPANEL_TOKEN) {
      if (currentUser) {
        // Tag events with the member's subscription_status. Mixpanel never
        // learns who they are (STACK-594). Once per sign-in: this effect
        // runs again when loading settles.
        if (analyticsUidRef.current !== currentUser.uid) {
          analyticsUidRef.current = currentUser.uid;
          identifyUser(currentUser.uid);
        }
      } else if (!loading) {
        analyticsUidRef.current = null;
        // Signed out, or a visitor: clear the status and start a new device
        // id. Not while Firebase is still restoring a session: every page
        // load starts with no user, and without identify() a member reset
        // there would become a new Mixpanel user on every reload.
        setSubscriptionStatus('unknown');
        mixpanel.reset();
      }
      // Re-apply Google Ads attribution after any identity change or reset —
      // reset() wipes all super properties so this must come after, not before.
      captureGoogleAdsAttribution();
      // Same post-reset rule: register app_platform as a super property so raw
      // mixpanel.track() events (landing_page_viewed,
      // subscription_checkout_session_*) carry it, not just events routed
      // through logAnalyticsEvent.
      mixpanel.register({ app_platform: window.appPlatform || 'web' });
    }
  }, [currentUser, loading]); // Only depend on auth state for Mixpanel tracking

  useEffect(() => {
    if (!MIXPANEL_TOKEN) {
      return;
    }

    const attributionProps = getGoogleAdsAttributionProps(location.search);
    if (Object.keys(attributionProps).length > 0) {
      mixpanel.track("ad_attributed_page_viewed", {
        page_path: location.pathname,
        ...attributionProps,
      });
      if (APP_ENVIRONMENT === "development"){
          console.log("Mixpanel event: ad_attributed_page_viewed");
      }
    }

    // Landing Page View tracking
    if (location.pathname === '/') {
      // Check if user is not logged in, as landing page might redirect for logged-in users
      // This depends on your routing logic for '/'
      // For now, we'll assume if they hit '/' before any redirect, it's a landing page view.
      mixpanel.track("landing_page_viewed", { 
        page_path: location.pathname,
        ...attributionProps,
        // You can add other properties like referrer if needed, e.g., document.referrer
      });
      if (APP_ENVIRONMENT === "development"){
          console.log("Mixpanel event: landing_page_viewed");
      }
    }
  }, [location.pathname, location.search]); // Re-run when path/query changes

  return (
    <HelmetProvider>
      <StackGenerationProvider>
        <FooterProvider initialHidden={shouldHideFooter(location.pathname)}>
          {/* Update notification for new app versions */}
          <UpdateNotification />
          
          <CssBaseline />
          <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            position="relative"
            // Keep page content clear of the fixed flat header. The logged-out
            // landing page is exempt so its hero bleeds up behind the
            // transparent bar.
            paddingTop={
              isPublicLanding
                ? 0
                : {
                    xs: 'calc(env(safe-area-inset-top) + 3.3rem)',
                    md: 'calc(env(safe-area-inset-top) + 3.55rem)',
                  }
            }
            // Keep page/footer content clear of the floating mobile bottom nav.
            paddingBottom={showMobileBottomNav ? "calc(5.25rem + env(safe-area-inset-bottom))" : 0}
            // That padding band has no content behind it, so the near-white
            // body background shows through. Invisible on light app pages, but
            // on the landing (dark evergreen footer) it reads as a white strip
            // under the floating nav — worse on device, where the safe-area
            // inset makes the band taller. Paint it evergreen there.
            bgcolor={isPublicLanding ? LC.evergreen : undefined}
          >
            <Header />
            <LapsedUserNotifications />
            <Box component="main" sx={{ flexGrow: 1 }}> 
            <Routes>
              <Route 
                path="/" 
                element={
                  loading ? (
                    <div>Loading...</div>
                  ) : currentUser ? (
                    <Navigate to="/dashboard" replace={true} />
                  ) : (
                    <LandingPage appPlatform={appPlatform} />
                  )
                } 
              />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/complete-registration" element={<RegistrationCompletion />} />
              <Route path="/subscribe" element={<SubscriptionRequired />} />
              <Route path="/home" element={<Login />} />
              <Route path="/conditions" element={<Conditions />}>
                <Route path=":conditionKey" element={<Detail />}>
                  <Route path="research" element={<Research />}>
                    <Route path=":articleId" element={<Article />} />
                  </Route>
                  {showAffiliateStore && <Route path="products" element={null} />}
                </Route>
              </Route>
              <Route path="/supplements" element={<Supplements />}>
                <Route path=":supplementKey" element={<Detail />}>
                  <Route path="research" element={<Research />}>
                    <Route path=":articleId" element={<Article />} />
                  </Route>
                  {showAffiliateStore && <Route path="products" element={null} />}
                </Route>
              </Route>
              
              {/* Product/Market routes */}
              {showAffiliateStore && <Route path="/products" element={<Navigate to="/market" replace />} />}
              {showAffiliateStore && <Route path="/market" element={<ProductsExplorer />} />}
              {showAffiliateStore && <Route path="/market/:supplementKey" element={<ProductsExplorer />} />}
              {showAffiliateStore && <Route path="/market/:supplementKey/:productSlug" element={<ProductDetail />} />}

              <Route path="/dashboard" element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              } />
              {/* The tabs are gone (STACK-515): recommended and built stacks
                  live in one view. `/dashboard/recommended-stacks` was a real,
                  linkable URL, so it redirects rather than 404s — bookmarks and
                  any link still in the wild land on the merged page. */}
              <Route path="/dashboard/:tab" element={<Navigate to="/dashboard" replace={true} />} />
              <Route path="/create-stack" element={<CreateStack />} />
              {/* Public and indexed: the interaction checker (STACK-587). */}
              <Route path="/supplement-interaction-checker" element={<InteractionCheckerPage />} />
              {/* **This path serves the Analyze Current Stack flow**, not a
                  plain manual editor — the route name is historical and stays
                  (ruled, STACK-544) so existing bookmarks keep working. The
                  member lists what they already take, names one health goal,
                  and gets back what the research says about it. It redirected
                  to /create-stack between 2026-09-12 and this relaunch, while
                  the feature was being redesigned. */}
              <Route path="/manual-create-stack" element={<ManualCreateStack />} />
              <Route path="/edit-stack/:stackId" element={<EditStack />} />
              <Route path="/view-stack/:stackId" element={<ViewStack />} />
              <Route path="/supplement-stacks/:goalSlug" element={<CuratedStackRedirect />} />
              <Route path="/stacks/medication-support" element={
                <RequireAuth>
                  <MedicationSupportView />
                </RequireAuth>
              } />
              <Route path="/stacks/diet-support" element={
                <RequireAuth>
                  <DietSupportView />
                </RequireAuth>
              } />
              <Route 
                path="/profile" 
                element={<ProfilePageRedesign />}
              />
              <Route path="/account-deleted" element={<AccountDeleted />} />
              {/* Public landing for Stripe checkout completed in Safari by the
                  iOS app (guideline 3.1.1 link-out) — must not require auth. */}
              <Route path="/subscription-complete" element={<SubscriptionComplete />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/download" element={<DownloadPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Blog routes enabled */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {isHealthPageEnabled(APP_ENVIRONMENT) && (
                <Route path="/health" element={<HealthPage />} />
              )}
            </Routes>
          </Box>
          <Footer />
        </Box>
        {showMobileBottomNav && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1300
            }}
          >
            <BottomNav />
          </Box>
        )}
      </FooterProvider>
      </StackGenerationProvider>
    </HelmetProvider>
  );
}

export default App;
